import React, {useEffect, useState, useRef} from 'react';
import {useRouter} from "next/router";
import {trackNavigation} from "helpers/analytics";
import {isEqual} from "lodash";
import {formatCityState} from "../../AuctionSearchPage/utils";
import {capitalizeWords} from "helpers/string";

const initialSearchState =  {
    paginatedAuctions: [],
    params_refill: {
        fill_vars: [
            'term', 
            'term_state', 
            'zipcode', 
            'type', 
            'radius', 
            'page_num',
        ],
        fill_vars_update: [
            'term', 
            'term_state'
        ],
    },
    search_obj: {
        page_count: 30,
        type: 'zipcode',
        page_num: 1,
        term: '',
        term_state: '',
        radius: 100,
    },
    search_obj_default: {
        type: 'zipcode',
        page_num: 1,
        term: '',
        term_state: '',
        radius: 100,
        term_state_label: ''
    },
    page_history: [1],
}

const useSearch = ({
    user_id,
    initialRender,
    searchLiveAuctions,
}) => {
    const firstUpdate = useRef(true);
    const router = useRouter();
    const [searchOnChange, setSearchOnChange] = useState(false);
    const [searchState, setSearchState] = useState({
        ...initialSearchState,
        search_obj: {
            ...initialSearchState.search_obj,
            ...JSON.parse(window.localStorage.getItem('live_auctions_search_obj')),
            term_state: router?.query?.state,
            term_city: router?.query?.city && `${capitalizeWords(router?.query?.city)}, ${router?.query?.state}`,
        },
        page_history: [1],
    })

    const redoSearch = ({
        search_obj_for_api: _search_obj_for_api,
        show_loading = true
    } = {}) => {
        const search_obj_for_api = _search_obj_for_api || searchState.search_obj;

        if (search_obj_for_api.type === 'city' && search_obj_for_api.term_city) {
            search_obj_for_api.term = search_obj_for_api.term_city
        }

        trackNavigation({ user_id });
    
        return searchLiveAuctions(search_obj_for_api, { show_loading })
    };

    const searchObjUpdated = ({ target: { name, value, label } }) => new Promise((resolve) => {
        setSearchState({
            ...searchState,
            search_obj: { ...searchState.search_obj, [name]: value, [name + '_label']: label },
        });

        resolve();
    })

    useEffect(() => {
        if (!isEqual(JSON.parse(window.localStorage.getItem('live_auctions_search_obj')), searchState.search_obj)) {
            window.localStorage.setItem('live_auctions_search_obj',
                JSON.stringify({
                    ...searchState.search_obj,
                })
            )
        }

        if (!initialRender.current) {
            window.localStorage.setItem('live_auctions_search_obj',
                JSON.stringify({
                    ...JSON.parse(window.localStorage.getItem('live_auctions_search_obj')),
                    ...searchState.search_obj,
                })
            )

            if (searchOnChange) redoSearch()
        }
    }, [searchState.search_obj])

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        redoSearch();
    }, [
        searchState.search_obj.page_num,
        searchState.search_obj.page_count,
    ]);

    useEffect(() => {
        if (window) {
            if (window.localStorage.getItem('live_auctions_search_obj')) {
                const newSearchObj = {
                    ...JSON.parse(window.localStorage.getItem('live_auctions_search_obj')),
                };

                if (router?.query?.state && !router?.query?.city) {
                    newSearchObj.type = 'state';
                    newSearchObj.term_state = router?.query?.state;
                    newSearchObj.term_state_label = router?.query?.state;
                }

                if (router?.query?.city && router?.query?.city !== 'undefined') {
                    newSearchObj.type = 'city';
                    newSearchObj.term_city = formatCityState(`${capitalizeWords(router?.query?.city)}, ${router?.query?.state}`)
                }

                setSearchState({
                    ...searchState,
                    search_obj: newSearchObj,
                    search_obj_default: JSON.parse(window.localStorage.getItem('live_auctions_search_obj'))
                })
            } else {
                setSearchState({
                    ...searchState,
                    search_obj: {
                        ...searchState.search_obj,
                        ...JSON.parse(window.localStorage.getItem('live_auctions_search_obj')),
                        term_state: router?.query?.state,
                        term_city: `${router?.query?.city}, ${router?.query?.state}`,
                    }
                })
            }
        }

        redoSearch();
    }, [])

    return {
        searchState,
        setSearchState,
        redoSearch,
        searchObjUpdated,
        initialSearchState,
    };
};

export default useSearch;